import {
  Array,
  BigDecimal,
  Cause,
  Clock,
  Data,
  Duration,
  Effect,
  Exit,
  Optic,
  Option,
  Schema,
} from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';
import * as AtomRegistry from 'effect/unstable/reactivity/AtomRegistry';

import { ProductNameText } from '@vs-shop/schemas/branded/ProductSchema';
import { Price, PriceFromApiSchema } from '@vs-shop/schemas/primitive/price';
import { CART_REFRESH_TIMEOUT, isUnknownWriteOutcome } from './cartTransport';

export type CartLinePreview = {
  readonly imageUrl?: string;
  readonly ageRestricted?: boolean;
};

/**
 * A line the shopper asked for that the backend has not confirmed yet. The cart
 * drawer and the navbar badge render it at once; the line disappears when the
 * refreshed order holds the real one, or when the backend refuses it.
 */
export type PendingCartLine = {
  readonly id: number;
  readonly variantCode: string;
  readonly productName: ProductNameText;
  readonly quantity: number;
  readonly totalPrice: Price;
  readonly preview?: CartLinePreview;
  /** The server quantity that confirms an accepted write. */
  readonly confirmationTarget: Option.Option<number>;
  /** The cart identity that answered an accepted write. */
  readonly confirmationCartToken: Option.Option<string>;
  /** The write outcome stays unknown until a successful cart read. */
  readonly unresolvedCause: Option.Option<Cause.Cause<unknown>>;
};

const confirmationTarget = Optic.id<PendingCartLine>().key('confirmationTarget');
const confirmationCartToken = Optic.id<PendingCartLine>().key('confirmationCartToken');
const unresolvedCause = Optic.id<PendingCartLine>().key('unresolvedCause');

/** A line the backend refused, or whose outcome stayed unknown. */
export type RejectedCartLine = {
  readonly line: PendingCartLine;
  readonly cause: Cause.Cause<unknown>;
};

class CartWriteSkippedError extends Data.TaggedError('@engine/Cart/WriteSkipped')<{
  readonly blockedByVariantCode: string;
}> {}

class CartPreflightRefreshError extends Data.TaggedError(
  '@engine/Cart/PreflightRefreshFailed'
)<{}> {}

class CartIdentityChangedError extends Data.TaggedError('@engine/Cart/IdentityChanged')<{}> {}

const settledCart = <D>(
  result: AsyncResult.AsyncResult<Option.Option<D>, unknown>
): Option.Option<D> =>
  AsyncResult.isSuccess(result) && !result.waiting ? result.value : Option.none();

export const makeOptimisticCartState = () => ({
  pendingCartLinesAtom: Atom.make<ReadonlyArray<PendingCartLine>>([]).pipe(Atom.keepAlive),
  rejectedCartLinesAtom: Atom.make<ReadonlyArray<RejectedCartLine>>([]).pipe(Atom.keepAlive),
});

export type OptimisticCartState = ReturnType<typeof makeOptimisticCartState>;

/** Keep an accepted line visible until a successful cart read confirms it. */
export const makeVisiblePendingCartLinesAtom = <D extends { readonly token: string }>(
  currentCartAtom: Atom.Atom<AsyncResult.AsyncResult<Option.Option<D>, unknown>>,
  quantityOf: (cart: D, variantCode: string) => number,
  pendingCartLinesAtom: OptimisticCartState['pendingCartLinesAtom']
) =>
  Atom.make((get) => {
    const cart = settledCart(get(currentCartAtom));
    return Array.filter(get(pendingCartLinesAtom), (line) => {
      const target = line.confirmationTarget;
      return Option.isNone(target)
        ? true
        : !Option.exists(
            cart,
            (known) =>
              Option.match(line.confirmationCartToken, {
                onNone: () => true,
                onSome: (token) => known.token === token,
              }) && quantityOf(known, line.variantCode) >= target.value
          );
    });
  });

/** A later successful write to this variant supersedes earlier accepted adds. */
export const retireAcceptedPendingLinesForVariant = (
  pendingCartLinesAtom: OptimisticCartState['pendingCartLinesAtom'],
  variantCode: string
) =>
  Effect.gen(function* () {
    const registry = yield* AtomRegistry.AtomRegistry;
    registry.update(pendingCartLinesAtom, (lines) =>
      Array.filter(
        lines,
        (line) => line.variantCode !== variantCode || Option.isNone(line.confirmationTarget)
      )
    );
  });

const sumQuantity = (lines: ReadonlyArray<PendingCartLine>) =>
  Array.reduce(lines, 0, (sum, line) => sum + line.quantity);

export const makePendingQuantityAtoms = (
  visiblePendingLinesAtom: Atom.Atom<ReadonlyArray<PendingCartLine>>
) => ({
  pendingQuantityAtom: Atom.map(visiblePendingLinesAtom, sumQuantity),
  pendingQuantityByVariantCodeAtom: Atom.family((variantCode: string) =>
    Atom.map(visiblePendingLinesAtom, (lines) =>
      sumQuantity(Array.filter(lines, (line) => line.variantCode === variantCode))
    )
  ),
});

export type CartLineRequest<P> = {
  readonly product: P;
  readonly quantity: number;
  readonly preview?: CartLinePreview;
};

type CartLineDescription = {
  readonly variantCode: string;
  readonly productName: string;
  /** Unit price in minor units, as the page showed it. */
  readonly unitPrice: number;
};

const decodeProductName = Schema.decodeSync(ProductNameText);
const decodeUnitPrice = Schema.decodeSync(PriceFromApiSchema);

export type AddToCartDeps<P, D extends { readonly token: string }, E, R, RT> = {
  readonly state: OptimisticCartState;
  /** The cart atom the command reads its baselines from and refreshes. */
  readonly cartAtom: Atom.Atom<AsyncResult.AsyncResult<Option.Option<D>, unknown>>;
  readonly quantityOf: (cart: D, variantCode: string) => number;
  readonly describe: (product: P) => CartLineDescription;
  /** The non-idempotent add; `quantityInCart` includes accepted writes awaiting a read. */
  readonly write: (request: CartLineRequest<P>, quantityInCart: number) => Effect.Effect<D, E, R>;
  /** Analytics for a line the backend holds. */
  readonly track: (request: CartLineRequest<P>) => Effect.Effect<void, never, RT>;
  readonly withPermit: <A, E1, R1>(effect: Effect.Effect<A, E1, R1>) => Effect.Effect<A, E1, R1>;
  readonly writeTimeout: Duration.Input;
  /** The delay before the first reconciliation read. Later reads back off. */
  readonly settleDelay: Duration.Input;
};

let nextPendingLineId = 0;

const hasUnknownWriteOutcome = (cause: Cause.Cause<unknown>) =>
  cause.reasons.some((reason) => Cause.isFailReason(reason) && isUnknownWriteOutcome(reason.error));

/**
 * Add-to-cart as an optimistic command.
 *
 * 1. The requested lines enter `pendingCartLinesAtom` synchronously, so the
 *    drawer and the badge show them before any request leaves.
 * 2. The writes run one after another under the cart permit. The backend's add
 *    is not idempotent, so a write is never repeated.
 * 3. A write that fails without an answer — a lost connection, a timeout, a
 *    gateway status — can still have committed. The command reads the cart
 *    until it confirms the line or the write deadline passes.
 * 4. A failed refresh keeps accepted lines pending until a later read confirms
 *    them. The real lines then replace them without an empty frame.
 * 5. A refused line lands in `rejectedCartLinesAtom` with its cause. The
 *    command still sends the lines after it. An unknown write stops the later
 *    lines if the cart cannot be read; each unsent line gets a rejection.
 * 6. The baseline that confirms a line comes from a settled cart read. If the
 *    cart atom is loading or failed, the command reads the cart first.
 *
 * The command succeeds with the first rejected line, if any; it never fails. Callers
 * learn about refusals through `rejectedCartLinesAtom`, because `Atom.fn`
 * interrupts a caller on the next call while the detached write goes on.
 *
 * `withPermit` runs its effect in a detached fiber. The command is uninterruptible.
 * The detached fiber lets `writeTimeout` interrupt a hung write.
 */
export const makeAddToCart = <P, D extends { readonly token: string }, E, R, RT>(
  deps: AddToCartDeps<P, D, E, R, RT>
) => {
  const { pendingCartLinesAtom, rejectedCartLinesAtom } = deps.state;
  const visibleLinesAtom = makeVisiblePendingCartLinesAtom(
    deps.cartAtom,
    deps.quantityOf,
    pendingCartLinesAtom
  );
  const observedRegistries = new WeakSet<AtomRegistry.AtomRegistry>();

  const observeConfirmedLines = (registry: AtomRegistry.AtomRegistry) => {
    if (observedRegistries.has(registry)) return;
    observedRegistries.add(registry);

    registry.subscribe(deps.cartAtom, (result) => {
      if (!AsyncResult.isSuccess(result) || result.waiting) return;

      const cart = result.value;
      const resolvedIds = new Set<number>();
      const rejected: RejectedCartLine[] = [];
      for (const line of registry.get(pendingCartLinesAtom)) {
        const target = line.confirmationTarget;
        if (Option.isNone(target)) continue;
        if (
          Option.isSome(cart) &&
          Option.isSome(line.confirmationCartToken) &&
          cart.value.token !== line.confirmationCartToken.value
        ) {
          resolvedIds.add(line.id);
          rejected.push({
            line,
            cause: Option.getOrElse(line.unresolvedCause, () =>
              Cause.fail(new CartIdentityChangedError())
            ),
          });
        } else if (
          Option.exists(
            cart,
            (known) =>
              Option.match(line.confirmationCartToken, {
                onNone: () => true,
                onSome: (token) => known.token === token,
              }) && deps.quantityOf(known, line.variantCode) >= target.value
          )
        ) {
          resolvedIds.add(line.id);
        } else if (Option.isSome(line.unresolvedCause)) {
          resolvedIds.add(line.id);
          rejected.push({ line, cause: line.unresolvedCause.value });
        } else if (Option.isNone(cart)) {
          resolvedIds.add(line.id);
        }
      }
      if (resolvedIds.size === 0) return;

      registry.update(pendingCartLinesAtom, (lines) =>
        Array.filter(lines, (line) => !resolvedIds.has(line.id))
      );
      if (rejected.length > 0) {
        registry.update(rejectedCartLinesAtom, (current) => [...current, ...rejected]);
      }
    });
  };

  const projectedQuantity = (
    registry: AtomRegistry.AtomRegistry,
    variantCode: string,
    currentIds: ReadonlySet<number>
  ) =>
    Option.match(settledCart(registry.get(deps.cartAtom)), {
      onNone: () => 0,
      onSome: (cart) => deps.quantityOf(cart, variantCode),
    }) +
    sumQuantity(
      Array.filter(
        registry.get(visibleLinesAtom),
        (line) =>
          line.variantCode === variantCode &&
          Option.isSome(line.confirmationTarget) &&
          !currentIds.has(line.id)
      )
    );

  const cartIsSettled = (registry: AtomRegistry.AtomRegistry) => {
    const result = registry.get(deps.cartAtom);
    if (!AsyncResult.isSuccess(result) || result.waiting) return false;
    const cart = result.value;
    if (Option.isNone(cart)) return true;
    return registry
      .get(pendingCartLinesAtom)
      .every(
        (line) =>
          Option.isNone(line.confirmationCartToken) ||
          line.confirmationCartToken.value === cart.value.token
      );
  };

  const waitForCartRefresh = (registry: AtomRegistry.AtomRegistry) =>
    AtomRegistry.getResult(registry, deps.cartAtom, { suspendOnWaiting: true }).pipe(
      Effect.timeoutOption(CART_REFRESH_TIMEOUT),
      Effect.option,
      Effect.map(Option.flatten)
    );

  const refreshCart = (registry: AtomRegistry.AtomRegistry) =>
    Effect.suspend(() => {
      registry.refresh(deps.cartAtom);
      return waitForCartRefresh(registry);
    });

  const reconcileUnknownWrite = (
    registry: AtomRegistry.AtomRegistry,
    variantCode: string,
    target: number,
    deadline: number
  ) =>
    Effect.gen(function* () {
      let delay = Duration.toMillis(deps.settleDelay);
      while (true) {
        const remaining = deadline - (yield* Clock.currentTimeMillis);
        if (remaining > 0) {
          yield* Effect.sleep(Duration.millis(Math.min(delay, remaining)));
        }

        const observed = yield* refreshCart(registry);
        if (
          Option.exists(
            Option.flatten(observed),
            (cart) => deps.quantityOf(cart, variantCode) >= target
          )
        ) {
          return observed;
        }
        if ((yield* Clock.currentTimeMillis) >= deadline) return observed;
        delay *= 2;
      }
    });

  const settle = (
    registry: AtomRegistry.AtomRegistry,
    requests: Array.NonEmptyReadonlyArray<CartLineRequest<P>>,
    lines: Array.NonEmptyReadonlyArray<PendingCartLine>,
    ids: ReadonlySet<number>
  ) =>
    Effect.gen(function* () {
      // Quantity this command committed that the cart atom does not show yet.
      const unseen = new Map<string, number>();
      let cartIsStale = false;
      let cartRefreshStarted = false;
      const unseenQuantity = (variantCode: string) =>
        Option.getOrElse(Option.fromNullishOr(unseen.get(variantCode)), () => 0);
      const markAccepted = (lineId: number, target: number, token: string) => {
        const current = registry.get(pendingCartLinesAtom);
        const superseded = Array.filter(
          current,
          (line) =>
            line.id !== lineId &&
            Option.isSome(line.confirmationTarget) &&
            Option.isSome(line.confirmationCartToken) &&
            line.confirmationCartToken.value !== token
        );
        const supersededIds = new Set(Array.map(superseded, (line) => line.id));

        registry.update(pendingCartLinesAtom, (lines) =>
          Array.map(
            Array.filter(lines, (line) => !supersededIds.has(line.id)),
            (line) =>
              line.id === lineId
                ? Optic.replace(
                    confirmationCartToken,
                    Option.some(token)
                  )(Optic.replace(confirmationTarget, Option.some(target))(line))
                : line
          )
        );
        if (superseded.length > 0) {
          const cause = Cause.fail(new CartIdentityChangedError());
          registry.update(rejectedCartLinesAtom, (rejected) => [
            ...rejected,
            ...Array.map(superseded, (line) => ({ line, cause })),
          ]);
        }
      };
      const markUnresolved = (
        lineId: number,
        target: number,
        cartToken: Option.Option<string>,
        cause: Cause.Cause<unknown>
      ) =>
        registry.update(pendingCartLinesAtom, (current) =>
          Array.map(current, (line) =>
            line.id === lineId
              ? Optic.replace(
                  unresolvedCause,
                  Option.some(cause)
                )(
                  Optic.replace(
                    confirmationCartToken,
                    cartToken
                  )(Optic.replace(confirmationTarget, Option.some(target))(line))
                )
              : line
          )
        );

      const rejections: RejectedCartLine[] = [];
      for (const [index, [request, line]] of Array.zip(requests, lines).entries()) {
        if (cartRefreshStarted || !cartIsSettled(registry)) {
          // The baseline must come from a cart read, not from an atom that is
          // still loading or failed without a previous value.
          if (cartRefreshStarted) {
            yield* waitForCartRefresh(registry);
            cartRefreshStarted = false;
          } else {
            yield* refreshCart(registry);
          }
          unseen.clear();
          cartIsStale = false;

          if (!cartIsSettled(registry)) {
            const result = registry.get(deps.cartAtom);
            const cause = AsyncResult.isFailure(result)
              ? result.cause
              : Cause.fail(new CartPreflightRefreshError());
            return [
              ...rejections,
              ...lines.slice(index).map((unsent) => ({ line: unsent, cause })),
            ];
          }
        }
        const baseline =
          projectedQuantity(registry, line.variantCode, ids) + unseenQuantity(line.variantCode);
        const baselineCartToken = Option.map(
          settledCart(registry.get(deps.cartAtom)),
          (cart) => cart.token
        );
        const writeStarted = yield* Clock.currentTimeMillis;
        const exit = yield* deps
          .write(request, baseline)
          .pipe(Effect.timeout(deps.writeTimeout), Effect.exit);

        if (Exit.isSuccess(exit)) {
          const confirmedQuantity = deps.quantityOf(exit.value, line.variantCode);
          const staleCartAlreadyConfirms = Option.exists(
            settledCart(registry.get(deps.cartAtom)),
            (known) =>
              known.token === exit.value.token &&
              deps.quantityOf(known, line.variantCode) >= confirmedQuantity
          );
          if (staleCartAlreadyConfirms) {
            registry.refresh(deps.cartAtom);
            cartRefreshStarted = true;
          }
          markAccepted(line.id, confirmedQuantity, exit.value.token);
          // A lower response quantity cannot use the old cart as confirmation.
          unseen.set(
            line.variantCode,
            confirmedQuantity - projectedQuantity(registry, line.variantCode, ids)
          );
          cartIsStale = true;
          yield* deps.track(request);
          continue;
        }

        if (!hasUnknownWriteOutcome(exit.cause)) {
          // A definite refusal: the backend did not take this line. The later
          // lines are independent requests, so the command still sends them.
          rejections.push({ line, cause: exit.cause });
          yield* refreshCart(registry);
          unseen.clear();
          cartIsStale = false;
          continue;
        }

        const observed = yield* reconcileUnknownWrite(
          registry,
          line.variantCode,
          baseline + request.quantity,
          writeStarted + Duration.toMillis(deps.writeTimeout)
        );
        unseen.clear();
        cartIsStale = false;

        if (Option.isNone(observed)) {
          markUnresolved(line.id, baseline + request.quantity, baselineCartToken, exit.cause);
          return [
            ...rejections,
            ...lines.slice(index + 1).map((unsent) => ({
              line: unsent,
              cause: Cause.fail(
                new CartWriteSkippedError({ blockedByVariantCode: line.variantCode })
              ),
            })),
          ];
        }

        const landed = Option.exists(
          Option.flatten(observed),
          (cart) => deps.quantityOf(cart, line.variantCode) >= baseline + request.quantity
        );

        if (!landed) {
          rejections.push({ line, cause: exit.cause });
          continue;
        }

        yield* Effect.logWarning(
          `Add to cart of ${line.variantCode} failed on the wire but the cart holds it`
        ).pipe(Effect.annotateLogs({ cause: Cause.pretty(exit.cause) }));
        const confirmedCart = Option.flatten(observed);
        if (Option.isNone(confirmedCart)) {
          rejections.push({ line, cause: exit.cause });
          continue;
        }
        markAccepted(line.id, baseline + request.quantity, confirmedCart.value.token);
        yield* deps.track(request);
      }

      if (cartIsStale) {
        if (cartRefreshStarted) {
          yield* waitForCartRefresh(registry);
        } else {
          yield* refreshCart(registry);
        }
      }

      return rejections;
    });

  return (requests: Array.NonEmptyReadonlyArray<CartLineRequest<P>>) =>
    Effect.gen(function* () {
      const registry = yield* AtomRegistry.AtomRegistry;
      observeConfirmedLines(registry);
      const lines = Array.map(requests, (request) => {
        const description = deps.describe(request.product);
        return {
          id: ++nextPendingLineId,
          variantCode: description.variantCode,
          productName: decodeProductName(description.productName),
          quantity: request.quantity,
          totalPrice: Price(
            BigDecimal.multiply(
              decodeUnitPrice(description.unitPrice),
              BigDecimal.fromNumberUnsafe(request.quantity)
            )
          ),
          ...(request.preview === undefined ? {} : { preview: request.preview }),
          confirmationTarget: Option.none<number>(),
          confirmationCartToken: Option.none<string>(),
          unresolvedCause: Option.none<Cause.Cause<unknown>>(),
        };
      });
      const ids = new Set(Array.map(lines, (line) => line.id));

      registry.update(rejectedCartLinesAtom, () => []);
      registry.update(pendingCartLinesAtom, (current) => [...current, ...lines]);

      const finish = (exit: Exit.Exit<ReadonlyArray<RejectedCartLine>>) =>
        Effect.sync(() => {
          const rejected = Exit.match(exit, {
            onSuccess: (rejections) => rejections,
            onFailure: (cause): ReadonlyArray<RejectedCartLine> => [
              { line: Array.headNonEmpty(lines), cause },
            ],
          });

          if (rejected.length > 0) {
            registry.update(rejectedCartLinesAtom, (current) => [...current, ...rejected]);
          }
          const visibleIds = new Set(Array.map(registry.get(visibleLinesAtom), (line) => line.id));
          registry.update(pendingCartLinesAtom, (current) =>
            Array.filter(
              current,
              (line) =>
                (Option.isSome(line.confirmationTarget) && visibleIds.has(line.id)) ||
                (!ids.has(line.id) && Option.isNone(line.confirmationTarget))
            )
          );
        });

      const rejections = yield* deps.withPermit(
        settle(registry, requests, lines, ids).pipe(Effect.onExit(finish))
      );
      return Array.head(rejections);
    }).pipe(
      // Registering the pending lines and starting their settlement is one
      // step: an interruption between the two would leave a line pending forever.
      Effect.uninterruptible
    );
};
